/**
 * Mutation source tracking — replaces the fragile `applyingRef` boolean.
 *
 * Tracks whether the current Zustand mutation was triggered locally (user action)
 * or by the server (incoming broadcast/diff). The Zustand subscription checks this
 * to prevent echo (re-pushing server data back to the server).
 *
 * This is synchronous — no async gap. The source is set around `setState()`,
 * so the subscription fires synchronously and reads the correct source.
 */

let _currentSource: 'local' | 'server' = 'local';

/** Returns the source of the currently-executing mutation. */
export function getMutationSource(): 'local' | 'server' {
  return _currentSource;
}

/**
 * Run a callback with the mutation source set to "server".
 * The Zustand subscription will see "server" and skip dirty detection.
 *
 * Handles nesting correctly (restores previous source on exit).
 */
export function withServerSource<T>(fn: () => T): T {
  const prev = _currentSource;
  _currentSource = 'server';
  try {
    return fn();
  } finally {
    _currentSource = prev;
  }
}
